import React, { useState, useEffect } from 'react';
import { TextField, InputAdornment, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import '../assets/styles/searchbox.css';
import { setSearchKeyword, clearFilters } from '../redux/slices/searchSlice';
import { search } from '../redux/slices/searchSlice';
import { ApiStatusKeys, clearApiStatus } from '../redux/slices/apiStatusSlice';
import ApiErrorModal from './common/api-error-modal';
import store from '../redux/store';
import { useAnalytics } from '../hooks/useAnalytics';

function SearchBox() {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const savedKeyword = useSelector((state) => state.search.keyword);
  const apiStatus = useSelector((state) => state.apiStatus[ApiStatusKeys.SEARCH]);
  const [searchTerm, setSearchTerm] = useState(savedKeyword || '');
  const [isLoading, setIsLoading] = useState(false);
  const { trackSearch } = useAnalytics();

  // Mobil görünümde kısa placeholder kullan
  const placeholderKey = isMobile ? 'homepage.searchbox-placeholder-mobile' : 'homepage.searchbox-placeholder';

  useEffect(() => {
    // Redux'taki keyword değiştiğinde searchTerm'i güncelle
    setSearchTerm(savedKeyword || '');
  }, [savedKeyword]);

  const handleSearch = async (e) => {
    if (e.key === 'Enter' && searchTerm.trim() !== '') {
      try {
        setIsLoading(true);

        dispatch(clearFilters());
        dispatch(setSearchKeyword(searchTerm));

        await search(dispatch, store.getState);

        // Track search analytics
        const searchResults = store.getState().search.results;
        trackSearch(searchTerm, searchResults?.length || 0);

        // Success - navigate to results
        if (location.pathname === '/') {
          navigate(`/search-results?q=${encodeURIComponent(searchTerm)}`);
        } else if (location.pathname === '/search-results') {
          navigate(`/search-results?q=${encodeURIComponent(searchTerm)}`, { replace: true });
        }
      } catch (error) {
        // Error - ApiStatusSlice zaten error'u set ediyor, modal otomatik açılacak
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    if (value.length <= 50) {
      setSearchTerm(value);
    }
  };

  const handleRetrySearch = async () => {
    // Retry search
    const mockEvent = { key: 'Enter' };
    await handleSearch(mockEvent);
  };

  return (
    <>
      <TextField
        placeholder={translate(placeholderKey)}
        variant="outlined"
        className="search-box"
        value={searchTerm}
        fullWidth
        inputProps={{ maxLength: 50 }}
        sx={{
          '& .MuiOutlinedInput-root': {
            width: '100%',
            '& fieldset': {
              width: '100%',
            },
            '& input': {
              width: '100%',
              padding: '10px 14px',
            },
          },
          '& .MuiInputBase-root': {
            width: '100%',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {isLoading ? <CircularProgress size={20} sx={{ color: 'primary.main' }} /> : <SearchIcon />}
            </InputAdornment>
          ),
        }}
        onChange={handleChange}
        onKeyDown={handleSearch}
      />

      {/* API Error Modal */}
      <ApiErrorModal
        requestKey={ApiStatusKeys.SEARCH}
        errorMessage={translate('errors.search_failed')}
        onClose={() => dispatch(clearApiStatus(ApiStatusKeys.SEARCH))}
        onRetry={handleRetrySearch}
      />
    </>
  );
}

export default SearchBox;
